import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { AlertTriangle, User, Calendar, Clock, Mail, ChevronRight, Plus, Archive, Filter, Search } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const STEP_LABELS = {
  1: "Rappel préventif",
  2: "Rappel ferme",
  3: "Dernier rappel",
  4: "Avertissement officiel"
};

const MOTIF_LABELS = {
  "absence": "Absence",
  "retard": "Retard",
  "performance": "Insuffisance professionnelle"
};

const STEP_COLORS = {
  1: "bg-yellow-100 text-yellow-800 border-yellow-300",
  2: "bg-orange-100 text-orange-800 border-orange-300",
  3: "bg-red-100 text-red-800 border-red-300",
  4: "bg-red-200 text-red-900 border-red-500"
};

const AdminWarnings = ({ user, activeModule = "b2b" }) => {
  const [warnings, setWarnings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userWarnings, setUserWarnings] = useState(null);
  const [filterMotif, setFilterMotif] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    user_id: '',
    user_name: '',
    motif: 'absence',
    step: 1,
    date: new Date().toISOString().split('T')[0],
    details: '',
    send_email: true
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [warningsRes, empRes, summaryRes] = await Promise.all([
        axios.get(`${API}/warnings`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/warnings/summary`, { withCredentials: true })
      ]);
      setWarnings(warningsRes.data);
      setEmployees(empRes.data.filter(e => e.role === 'employee' && e.active !== false));
      setSummary(summaryRes.data.summary || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserWarnings = async (userId, updateFormStep = false) => {
    try {
      const res = await axios.get(`${API}/warnings/user/${userId}`, { withCredentials: true });
      setUserWarnings(res.data);
      setSelectedUser(userId);
      
      // Si demandé, mettre à jour l'étape du formulaire avec la prochaine étape logique
      if (updateFormStep && res.data) {
        const currentStep = res.data.current_steps[formData.motif] || 0;
        const nextStep = Math.min(currentStep + 1, 4);
        setFormData(prev => ({
          ...prev,
          step: nextStep
        }));
      }
      
      return res.data;
    } catch (error) {
      console.error('Failed to fetch user warnings:', error);
      return null;
    }
  };

  const handleEmployeeSelect = async (e) => {
    const emp = employees.find(emp => emp.user_id === e.target.value);
    if (emp) {
      const userName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name;
      setFormData(prev => ({
        ...prev,
        user_id: emp.user_id,
        user_name: userName
      }));
      
      // Charger les warnings de l'employé et mettre à jour l'étape
      const data = await fetchUserWarnings(emp.user_id);
      if (data) {
        const currentStep = data.current_steps[formData.motif] || 0;
        const nextStep = Math.min(currentStep + 1, 4);
        setFormData(prev => ({
          ...prev,
          step: nextStep
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/warnings`, formData, { withCredentials: true });
      alert(response.data.message);
      fetchAll();
      if (selectedUser) {
        fetchUserWarnings(selectedUser);
      }
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create warning:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleArchive = async (warningId) => {
    if (!window.confirm('Archiver ce rappel/avertissement ?')) return;
    try {
      await axios.put(`${API}/warnings/${warningId}/archive`, {}, { withCredentials: true });
      fetchAll();
      if (selectedUser) {
        fetchUserWarnings(selectedUser);
      }
    } catch (error) {
      console.error('Failed to archive warning:', error);
      toast.error(`Archivage non effectué : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      user_name: '',
      motif: 'absence',
      step: 1,
      date: new Date().toISOString().split('T')[0],
      details: '',
      send_email: true
    });
    setUserWarnings(null);
    setSelectedUser(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getNextStep = (motif) => {
    if (!userWarnings) return 1;
    const currentStep = userWarnings.current_steps[motif] || 0;
    return Math.min(currentStep + 1, 4);
  };

  // Filter warnings
  const filteredWarnings = warnings.filter(w => {
    if (filterMotif && w.motif !== filterMotif) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return w.user_name?.toLowerCase().includes(searchLower) ||
             w.motif?.toLowerCase().includes(searchLower);
    }
    return true;
  });

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A]">Rappels & Avertissements</h1>
              <p className="text-sm text-gray-500">Système progressif: Rappel → Avertissement</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-[#0F172A] hover:bg-[#1e293b]"
            data-testid="new-warning-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouveau rappel
          </Button>
        </div>

        {/* Summary Cards */}
        {summary.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">Résumé par salarié</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summary.slice(0, 6).map((s) => (
                <Card 
                  key={s._id} 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => fetchUserWarnings(s._id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{s.user_name}</span>
                      <span className="text-sm text-gray-500">{s.total_warnings} rappel(s)</span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      {s.max_step_absence > 0 && (
                        <span className={`px-2 py-1 rounded ${STEP_COLORS[s.max_step_absence]}`}>
                          Absence: {s.max_step_absence}/4
                        </span>
                      )}
                      {s.max_step_retard > 0 && (
                        <span className={`px-2 py-1 rounded ${STEP_COLORS[s.max_step_retard]}`}>
                          Retard: {s.max_step_retard}/4
                        </span>
                      )}
                      {s.max_step_performance > 0 && (
                        <span className={`px-2 py-1 rounded ${STEP_COLORS[s.max_step_performance]}`}>
                          Perf: {s.max_step_performance}/4
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Rechercher un salarié..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={filterMotif}
            onChange={(e) => setFilterMotif(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">Tous les motifs</option>
            <option value="absence">Absence</option>
            <option value="retard">Retard</option>
            <option value="performance">Performance</option>
          </select>
        </div>

        {/* Warnings List */}
        <div className="grid gap-4">
          {filteredWarnings.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Aucun rappel/avertissement</p>
              </CardContent>
            </Card>
          ) : (
            filteredWarnings.map((warning) => (
              <Card key={warning.warning_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold">{warning.user_name}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${STEP_COLORS[warning.step]}`}>
                          {STEP_LABELS[warning.step]}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                          {MOTIF_LABELS[warning.motif]}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(warning.date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(warning.created_at)}
                        </span>
                        {warning.email_sent && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Mail className="w-3 h-3" />
                            Email envoyé
                          </span>
                        )}
                      </div>
                      
                      {warning.details && (
                        <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded mt-2">
                          {warning.details}
                        </p>
                      )}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleArchive(warning.warning_id)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* User Detail Modal */}
        {userWarnings && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">
                  Historique: {userWarnings.user_name}
                </h2>
                <button
                  onClick={() => { setUserWarnings(null); setSelectedUser(null); }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  ×
                </button>
              </div>
              
              <div className="p-4">
                {/* Current Steps */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {Object.entries(MOTIF_LABELS).map(([motif, label]) => (
                    <div key={motif} className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">{label}</p>
                      <p className="text-2xl font-bold">
                        {userWarnings.current_steps[motif] || 0}/4
                      </p>
                      <div className="flex justify-center gap-1 mt-2">
                        {[1, 2, 3, 4].map(step => (
                          <div
                            key={step}
                            className={`w-3 h-3 rounded-full ${
                              step <= (userWarnings.current_steps[motif] || 0)
                                ? STEP_COLORS[step].replace('text-', 'bg-').split(' ')[0]
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Warnings History */}
                <h3 className="font-semibold mb-3">Historique complet</h3>
                <div className="space-y-2">
                  {userWarnings.warnings.map((w) => (
                    <div key={w.warning_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`w-2 h-2 rounded-full ${STEP_COLORS[w.step].split(' ')[0]}`} />
                      <span className="text-sm">{formatDate(w.date)}</span>
                      <span className="text-sm font-medium">{MOTIF_LABELS[w.motif]}</span>
                      <span className={`text-xs px-2 py-1 rounded ${STEP_COLORS[w.step]}`}>
                        {STEP_LABELS[w.step]}
                      </span>
                      {w.email_sent && <Mail className="w-3 h-3 text-green-500" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">Nouveau rappel/avertissement</h2>
                <button
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {/* Employee Select */}
                <div>
                  <Label className="font-semibold">Salarié *</Label>
                  <select
                    value={formData.user_id}
                    onChange={handleEmployeeSelect}
                    required
                    className="w-full mt-1 h-10 px-3 border border-gray-200 rounded-md"
                  >
                    <option value="">Sélectionner un salarié</option>
                    {employees.map(emp => (
                      <option key={emp.user_id} value={emp.user_id}>
                        {emp.first_name} {emp.last_name} ({emp.name})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Current steps display */}
                {userWarnings && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">Étapes actuelles:</p>
                    <div className="flex gap-4 text-sm">
                      {Object.entries(MOTIF_LABELS).map(([motif, label]) => (
                        <span key={motif}>
                          {label}: <strong>{userWarnings.current_steps[motif] || 0}/4</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Motif */}
                <div>
                  <Label className="font-semibold">Motif *</Label>
                  <select
                    value={formData.motif}
                    onChange={(e) => {
                      const motif = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        motif,
                        step: getNextStep(motif)
                      }));
                    }}
                    required
                    className="w-full mt-1 h-10 px-3 border border-gray-200 rounded-md"
                  >
                    <option value="absence">Absence</option>
                    <option value="retard">Retard</option>
                    <option value="performance">Insuffisance professionnelle</option>
                  </select>
                </div>

                {/* Step (auto-calculated) */}
                <div>
                  <Label className="font-semibold">Étape</Label>
                  <div className={`mt-1 p-3 rounded-lg ${STEP_COLORS[formData.step]}`}>
                    <span className="font-medium">{STEP_LABELS[formData.step]}</span>
                    <span className="text-sm ml-2">(Étape {formData.step}/4)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    L'étape est calculée automatiquement (étape suivante du dernier rappel)
                  </p>
                </div>

                {/* Date */}
                <div>
                  <Label className="font-semibold">Date de l'incident *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    required
                    className="mt-1"
                  />
                </div>

                {/* Details */}
                <div>
                  <Label className="font-semibold">Détails / Commentaires</Label>
                  <textarea
                    value={formData.details}
                    onChange={(e) => setFormData(prev => ({ ...prev, details: e.target.value }))}
                    placeholder="Détails de l'incident..."
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm"
                    rows={3}
                  />
                </div>

                {/* Send Email */}
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="send_email_warning"
                    checked={formData.send_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, send_email: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <label htmlFor="send_email_warning" className="text-sm">
                    <Mail className="w-4 h-4 inline mr-1 text-blue-600" />
                    Envoyer automatiquement un email au salarié
                  </label>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-[#0F172A]" data-testid="submit-warning-btn">
                    Créer le rappel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminWarnings;
